/* eslint-disable react-hooks/exhaustive-deps */

import { withStyles } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useDropzone } from 'react-dropzone';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { BANK_PAYMENT_TYPES, EXPENSES_BATCH_TYPES, EXPENSES_PAYEE_TYPE, ZERO_GUID } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { getExpenseCodesByClientId, getVendorDetails } from 'modules/authorizations/store/actions/dataManagement.actions';
import { getClientDetails, setClientId } from 'modules/clients/clients.actions';
import { GET_VENDOR_DETAILS_FAILURE } from 'modules/authorizations/store/types/dataManagementTypes';
import { showToast } from 'modules/layout/layout.actions';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { createEquityReport, createEquityVouchers, getHomesaleInfo } from 'modules/authorizations/store/actions/homesale.actions';
import { CreateExpenseCodeSearchInfo } from 'modules/common/expenseCodeHelper';
import { CREATE_EQUITY_VOUCHERS_FAILURE, CREATE_EQUITY_VOUCHERS_REPORT_FAILURE } from 'modules/authorizations/store/types/homesaleTypes';
import AdvancedSearchComponent from 'modules/common/advancedSearch.component';
import SpinnerComponent from 'modules/common/spinner.component';

const styles = theme => ({
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  }
});

const EquityVoucherWizard = props => {
  const {
    equityId,
    equityInfo,
    authFileId,
    homesaleId,
    setShowPostVoucher,
    vendors,
    authBanks,
    getVendorDetails,
    expenseCodes,
    clientId,
    isReadOnly,
    authFileNumber
  } = props;

  const codeSearchInfo = useMemo(() => CreateExpenseCodeSearchInfo(expenseCodes || []), [expenseCodes]);
  const ecById = id => expenseCodes.find(e => Number(e.expenseCodeId) === Number(id));

  const codeDisplayFromId = id => {
    const ec = ecById(id);
    if (!ec) return '';
    const code = ec.expenseCodeCode || ec.expenseCode || '';
    const desc = ec.expenseCodeDescription || ec.description || '';
    return desc ? code + ' – ' + desc : code;
  };

  const getPaymentSettingsFromBank = paymentTypeId => {
    switch ((paymentTypeId || '').toUpperCase()) {
      case BANK_PAYMENT_TYPES.WIRE:
        return {
          batchTypeId: EXPENSES_BATCH_TYPES.ACH_WIRE,
          isWireTransfer: true
        };

      case BANK_PAYMENT_TYPES.ACH:
        return {
          batchTypeId: EXPENSES_BATCH_TYPES.ACH_WIRE,
          isWireTransfer: false
        };

      case BANK_PAYMENT_TYPES.INTERNATIONAL_WIRE:
        return {
          batchTypeId: EXPENSES_BATCH_TYPES.INTERNATIONAL,
          isWireTransfer: true
        };

      case BANK_PAYMENT_TYPES.CHECK_PAYMENT:
        return {
          batchTypeId: EXPENSES_BATCH_TYPES.CHECK,
          isWireTransfer: false
        };

      default:
        return {
          batchTypeId: EXPENSES_BATCH_TYPES.ACH_WIRE,
          isWireTransfer: false
        };
    }
  };

  const creditsAndCharges = equityInfo.creditCharges || [];
  const homesaleEquityCalculations = equityInfo.homesaleEquityCalculations || {};
  const equityDetails = equityInfo.equityInfo || {};
  const isNegativeEquity = (homesaleEquityCalculations.totalEquityUnpaid || 0) <= 0;
  const steps = isNegativeEquity ? ['Confirm Amount', 'Review Report'] : ['Confirm Amount', 'Split Equity', 'Voucher Details', 'Review Report'];

  const isAdvanceType = isIdEqual(equityDetails.equityStatusId, '7D8D9942-DCEA-4A88-BC35-8D8BC38CAFEE');
  const isFinalType = isIdEqual(equityDetails.equityStatusId, '560008D2-3974-43AF-8639-97CB6410011B');

  const isStatementRecordType = (equityDetails.equityRecordType || '').toUpperCase() === 'S';

  const defaultExpenseCodeId = isStatementRecordType ? expenseCodes.find(ec => ec.expenseCodeCode === '8710')?.expenseCodeId : isAdvanceType
    ? expenseCodes.find(ec => ec.expenseCodeCode === '2904')?.expenseCodeId
    : isFinalType
      ? expenseCodes.find(ec => ec.expenseCodeCode === '2900')?.expenseCodeId
      : null;

  const [activeStep, setActiveStep] = useState(0);

  const [amountMode, setAmountMode] = useState('full');
  const [customMode, setCustomMode] = useState('amount');
  const [customAmount, setCustomAmount] = useState('');
  const [customPercent, setCustomPercent] = useState('');

  const [splitMode, setSplitMode] = useState('no');
  const [splitEntryMode, setSplitEntryMode] = useState('percent');
  const [voucher1Percent, setVoucher1Percent] = useState('50');
  const [voucher1Amount, setVoucher1Amount] = useState('');

  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [attachmentVoucherIndex, setAttachmentVoucherIndex] = useState(null);
  const [temporaryAttachments, setTemporaryAttachments] = useState([]);

  const [vendorBanksByIndex, setVendorBanksByIndex] = useState({
    0: [],
    1: []
  });

  const [generatedReport, setGeneratedReport] = useState(null);
  const [reportIsLoading, setReportIsLoading] = useState(false);
  const [isSavingVouchers, setIsSavingVouchers] = useState(false);

  const loadPreview = async () => {
    setReportIsLoading(true);
    const vouchersToSend = await Promise.all(
      visibleVoucherDetails.map(async (voucher, index) => {
        const splitValue = splitValues[index] || { amount: 0, percent: 0 };
        return {
          ...voucher,
          postedAmount: splitValue.amount
        };
      })
    );

    const request = {
      ...equityVouchersRequest,
      equityAmountToBePaidInTotal: selectedPostAmount,
      percentageToBePaid: totals.remainingToPost ? (selectedPostAmount / totals.remainingToPost) * 100 : 0,
      isSplitPaymentBetweenVouchers: splitMode === 'yes',
      equityVouchers: vouchersToSend
    };

    const resp = await props.createEquityReport(authFileId, homesaleId, equityId, request);
    if (resp.type === CREATE_EQUITY_VOUCHERS_REPORT_FAILURE) {
      props.showToast('Failed to generate report preview, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      setReportIsLoading(false);
      return;
    }

    const pdfBlob = resp.response instanceof Blob ? resp.response : new Blob([resp.response], { type: 'application/pdf' });

    if (generatedReport?.url) {
      URL.revokeObjectURL(generatedReport.url);
    }

    const fileName = `equity-settlement-${equityId}.pdf`;
    const url = URL.createObjectURL(pdfBlob);

    setGeneratedReport({ fileName, url });
    setReportIsLoading(false);
  };

  const openAttachmentDialog = index => {
    setAttachmentVoucherIndex(index);
    setTemporaryAttachments(equityVouchers[index]?.attachments || []);
    setAttachmentDialogOpen(true);
  };

  const handleCloseAttachmentDialog = () => {
    setAttachmentDialogOpen(false);
    setAttachmentVoucherIndex(null);
    setTemporaryAttachments([]);
  };

  const handleSaveAttachmentDialog = () => {
    if (attachmentVoucherIndex === null) return;

    setEquityVouchers(prev =>
      prev.map((item, i) => {
        if (i !== attachmentVoucherIndex) return item;
        return {
          ...item,
          attachments: temporaryAttachments
        };
      })
    );

    handleCloseAttachmentDialog();
  };

  const onDropAttachments = acceptedFiles => {
    setTemporaryAttachments(prev => [...prev, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: onDropAttachments,
    multiple: true
  });

  const removeTemporaryAttachment = indexToRemove => {
    setTemporaryAttachments(prev => prev.filter((_, i) => i !== indexToRemove));
  };

  const convertToBase64 = file => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result);
      };

      fileReader.onerror = error => {
        reject(error);
      };
    });
  }; //Monday: just change the postvoucherline, and delete voucher line to return the same thing as usp_GetAllVoucherLineAttachments
  /// Then just copy this logic for expenses and recurring payables

  const mapFilesToAttachmentDtos = async files => {
    if (!files || files.length === 0) return [];

    const uploaded = await Promise.all(
      files.map(async file => {
        let base64 = await convertToBase64(file);
        base64 = base64.split(',')[1];

        return {
          fileName: file.name,
          contentType: file.type,
          base64FileData: base64
        };
      })
    );

    return uploaded;
  };

  const removeVoucherAttachment = (voucherIndex, fileIndex) => {
    setEquityVouchers(prev =>
      prev.map((item, i) => {
        if (i !== voucherIndex) return item;
        return {
          ...item,
          attachments: (item.attachments || []).filter((_, idx) => idx !== fileIndex)
        };
      })
    );
  };

  useEffect(() => {
    const fetchCodes = async () => {
      let expenseCodeClientId = null;
      if (clientId) {
        props.setClientId(clientId);
        const clientResp = await props.getClientDetails(clientId);
        expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
      }

      if (expenseCodeClientId) {
        await props.getExpenseCodesByClientId(expenseCodeClientId);
      }
    };

    fetchCodes();
  }, [clientId]);

  const isVendorPayee = voucher => isIdEqual(voucher.payeeTypeId, EXPENSES_PAYEE_TYPE.VENDOR);
  const isTransfereePayee = voucher => isIdEqual(voucher.payeeTypeId, EXPENSES_PAYEE_TYPE.TRANSFEREE);

  const handleCreateVouchers = async () => {
    setIsSavingVouchers(true);
    const vouchersToSend = await Promise.all(
      visibleVoucherDetails.map(async (voucher, index) => {
        const splitValue = splitValues[index] || { amount: 0, percent: 0 };
        const attachments = await mapFilesToAttachmentDtos(voucher.attachments || []);

        return {
          ...voucher,
          postedAmount: splitValue.amount,
          attachments
        };
      })
    );

    const request = {
      ...equityVouchersRequest,
      equityAmountToBePaidInTotal: selectedPostAmount,
      percentageToBePaid: totals.remainingToPost ? (selectedPostAmount / totals.remainingToPost) * 100 : 0,
      isSplitPaymentBetweenVouchers: splitMode === 'yes',
      equityVouchers: vouchersToSend
    };

    const resp = await props.createEquityVouchers(authFileId, homesaleId, equityId, request);

    if (resp.type === CREATE_EQUITY_VOUCHERS_FAILURE) {
      props.showToast('Failed to create equity vouchers, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
    } else {
      await props.getHomesaleInfo(authFileId, homesaleId);
      props.showToast('Equity vouchers created successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });
      setShowPostVoucher(false);
    }
    setIsSavingVouchers(false);
  };

  let equityVouchersRequest = {
    equityId: equityId,
    authorizationFileId: authFileId,
    equityTotalAmountWhenPosted: homesaleEquityCalculations.totalEquityAmount,
    equityAmountToBePaidInTotal: 0,
    percentageToBePaid: 0,
    isSplitPaymentBetweenVouchers: false,
    datePosted: new Date().toISOString(),
    authFileNumber: authFileNumber
  };

  const [equityVouchers, setEquityVouchers] = useState([
    {
      voucherOrderNumber: 1,
      payeeTypeId: EXPENSES_PAYEE_TYPE.TRANSFEREE,
      payToId: authFileId,
      clientId: clientId,
      postedAmount: 0,
      description: defaultExpenseCodeId ? codeDisplayFromId(defaultExpenseCodeId) : '',
      invoiceDate: new Date().toISOString(),
      entryDate: new Date().toISOString(),
      receiptCurrencyId: ZERO_GUID,
      payInCurrencyId: ZERO_GUID,
      currencyExchangeRate: 0,
      currencyExchangeRateDate: null,
      batchTypeId: EXPENSES_BATCH_TYPES.ACH_WIRE,
      isWireTransfer: false,
      payeeAccount: null,
      expenseCode: defaultExpenseCodeId,
      attachments: [],
      emailBody: ''
    },
    {
      voucherOrderNumber: 2,
      payeeTypeId: EXPENSES_PAYEE_TYPE.TRANSFEREE,
      payToId: authFileId,
      clientId: clientId,
      postedAmount: 0,
      description: defaultExpenseCodeId ? codeDisplayFromId(defaultExpenseCodeId) : '',
      invoiceDate: new Date().toISOString(),
      entryDate: new Date().toISOString(),
      receiptCurrencyId: ZERO_GUID,
      payInCurrencyId: ZERO_GUID,
      currencyExchangeRate: 0,
      currencyExchangeRateDate: null,
      batchTypeId: EXPENSES_BATCH_TYPES.ACH_WIRE,
      isWireTransfer: false,
      payeeAccount: null,
      expenseCode: defaultExpenseCodeId,
      attachments: [],
      emailBody: ''
    }
  ]);

  const transformCreditsAndCharges = (creditsAndCharges, equityCalcs) => {
    let resp = [
      {
        amount: equityCalcs.purchasePrice,
        id: 'purchasePrice',
        type: 'Credit',
        code: '',
        description: 'Purchase Price'
      },
      {
        amount: equityCalcs.mortgageOneAmount,
        id: 'mortgageOneAmount',
        type: 'Charge',
        code: '',
        description: 'First Mortgage Amt'
      },
      {
        amount: equityCalcs.mortgageOneInterestProrationAmount,
        id: 'mortgageOneInterestProrationAmount',
        type: 'Charge',
        code: '',
        description: 'First Interest Proration Amt'
      },
      {
        amount: equityCalcs.mortgageTwoAmount,
        id: 'mortgageTwoAmount',
        type: 'Charge',
        code: '',
        description: 'Second Mortgage Amt'
      },
      {
        amount: equityCalcs.mortgageTwoInterestProrationAmount,
        id: 'mortgageTwoInterestProrationAmount',
        type: 'Charge',
        code: '',
        description: 'Second Interest Proration Amt'
      }
    ];

    resp = resp.concat(
      creditsAndCharges.map(row => ({
        ...row,
        amount: Number(row.amount || 0),
        id: row.homesaleEquityCreditChargeId,
        type: isIdEqual(row.creditChargeTypeId, '936FC501-7A03-4C78-83D5-7AC32113D426') ? 'Credit' : 'Charge',
        code: expenseCodes.find(s => s.expenseCodeId === row.expenseCodeId)?.expenseCodeCode || '',
        description: expenseCodes.find(s => s.expenseCodeId === row.expenseCodeId)?.expenseCodeDescription || ''
      }))
    );

    return resp;
  };

  const creditAndChargesRows = transformCreditsAndCharges(creditsAndCharges, homesaleEquityCalculations);

  const currency = value =>
    Number(value || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });

  const totals = useMemo(() => {
    return {
      totalEquity: homesaleEquityCalculations.totalEquityAmount,
      alreadyPaid: homesaleEquityCalculations.totalEquityPaid,
      remainingToPost: homesaleEquityCalculations.totalEquityUnpaid,
      remainingBalanceAfter: homesaleEquityCalculations.totalEquityUnpaid - selectedPostAmount
    };
  }, [homesaleEquityCalculations]);

  const selectedPostAmount = useMemo(() => {
    if (amountMode === 'full') return totals.remainingToPost;

    if (customMode === 'amount') return Number(customAmount || 0);

    return (totals.remainingToPost * Number(customPercent || 0)) / 100;
  }, [amountMode, customMode, customAmount, customPercent, totals.remainingToPost]);

  const splitValues = useMemo(() => {
    if (splitMode !== 'yes') {
      return [
        {
          id: 1,
          amount: selectedPostAmount,
          percent: 100
        }
      ];
    }

    if (splitEntryMode === 'percent') {
      const v1 = Number(voucher1Percent || 0);
      const v2 = 100 - v1;

      return [
        {
          id: 1,
          amount: (selectedPostAmount * v1) / 100,
          percent: v1
        },
        {
          id: 2,
          amount: (selectedPostAmount * v2) / 100,
          percent: v2
        }
      ];
    }

    const v1Amount = Number(voucher1Amount || 0);
    const v2Amount = selectedPostAmount - v1Amount;

    return [
      {
        id: 1,
        amount: v1Amount,
        percent: selectedPostAmount !== 0 ? (v1Amount / selectedPostAmount) * 100 : 0
      },
      {
        id: 2,
        amount: v2Amount,
        percent: selectedPostAmount !== 0 ? (v2Amount / selectedPostAmount) * 100 : 0
      }
    ];
  }, [splitMode, splitEntryMode, voucher1Percent, voucher1Amount, selectedPostAmount]);

  const visibleVoucherDetails = splitMode === 'yes' ? equityVouchers : [equityVouchers[0]];

  const handleNext = () => {
    if (activeStep < steps.length - 1) setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const handleVoucherFieldChange = (index, field, value) => {
    updateVoucher(index, { [field]: value });
  };

  const handlePayeeTypeChange = (index, payeeTypeId) => {
    const isTransferee = isIdEqual(payeeTypeId, EXPENSES_PAYEE_TYPE.TRANSFEREE);

    updateVoucher(index, {
      payeeTypeId,
      payToId: isTransferee ? authFileId : null,
      payeeAccount: null
    });

    setVendorBanksByIndex(prev => ({
      ...prev,
      [index]: []
    }));
  };

  const updateVoucher = (index, updates) => {
    setEquityVouchers(prev => prev.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const getVendorName = vendorId => {
    return vendors.find(v => v.vendorId === vendorId)?.companyName || '';
  };

  const handleVendorSelect = async (index, vendorId) => {
    updateVoucher(index, {
      payToId: vendorId,
      payeeAccount: null
    });

    setVendorBanksByIndex(prev => ({
      ...prev,
      [index]: []
    }));

    const resp = await getVendorDetails(vendorId);
    if (resp.type === GET_VENDOR_DETAILS_FAILURE) {
      props.showToast('Failed to retrieve vendor info, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
      return;
    }

    setVendorBanksByIndex(prev => ({
      ...prev,
      [index]: resp.response?.banks || []
    }));
  };

  const openPreviewWindow = () => {
    if (!generatedReport?.url) return;
    window.open(generatedReport.url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    return () => {
      if (generatedReport?.url) {
        URL.revokeObjectURL(generatedReport.url);
      }
    };
  }, [generatedReport]);

  const handleTransfereeAccountSelect = (index, bankId) => {
    const selectedBank = authBanks.find(b => Number(b.bankId) === Number(bankId));
    const paymentSettings = getPaymentSettingsFromBank(selectedBank?.paymentTypeId);

    updateVoucher(index, {
      payeeAccount: bankId,
      batchTypeId: paymentSettings.batchTypeId,
      isWireTransfer: paymentSettings.isWireTransfer
    });
  };

  const handleVendorAccountSelect = (index, bankId) => {
    const selectedBank = (vendorBanksByIndex[index] || []).find(b => Number(b.bank.bankId) === Number(bankId));
    const paymentSettings = getPaymentSettingsFromBank(selectedBank?.bank?.paymentTypeId);

    updateVoucher(index, {
      payeeAccount: bankId,
      batchTypeId: paymentSettings.batchTypeId,
      isWireTransfer: paymentSettings.isWireTransfer
    });
  };

  const renderStep1 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Step 1: Confirm amount to post
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditAndChargesRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.code}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">{currency(row.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Calculated amounts
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, mb: 3 }}>
            <Typography>Total equity</Typography>
            <Typography>{currency(totals.totalEquity)}</Typography>

            <Typography>Already paid (2900 / 2904)</Typography>
            <Typography>{currency(totals.alreadyPaid)}</Typography>

            <Typography sx={{ fontWeight: 700 }}>Remaining to post</Typography>
            <Typography sx={{ fontWeight: 700 }}>{currency(totals.remainingToPost)}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Posting option
          </Typography>

          <RadioGroup value={amountMode} onChange={e => setAmountMode(e.target.value)}>
            <FormControlLabel value="full" control={<Radio />} label="Post full amount" />
            <FormControlLabel value="custom" control={<Radio />} label="Post custom amount" />
          </RadioGroup>

          {amountMode === 'custom' && (
            <Box sx={{ mt: 2 }}>
              <RadioGroup row value={customMode} onChange={e => setCustomMode(e.target.value)}>
                <FormControlLabel value="amount" control={<Radio />} label="Enter amount" />
                <FormControlLabel value="percent" control={<Radio />} label="Enter percent" />
              </RadioGroup>

              {customMode === 'amount' && (
                <TextField
                  fullWidth
                  label="Custom Amount"
                  value={customAmount}
                  onChange={e => setCustomAmount(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}

              {customMode === 'percent' && (
                <TextField
                  fullWidth
                  label="Custom Percent"
                  value={customPercent}
                  onChange={e => setCustomPercent(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          )}

          <Box sx={{ mt: 3, p: 1.5, backgroundColor: '#f7f7f7', borderRadius: '4px' }}>
            <Typography variant="body2">Selected amount to post</Typography>
            <Typography variant="h6">{currency(selectedPostAmount)}</Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Step 2: Confirm whether to split the equity
          </Typography>

          <RadioGroup value={splitMode} onChange={e => setSplitMode(e.target.value)}>
            <FormControlLabel value="no" control={<Radio />} label="Do not split" />
            <FormControlLabel value="yes" control={<Radio />} label="Split into Voucher 1 and Voucher 2" />
          </RadioGroup>

          {splitMode === 'yes' && (
            <Box sx={{ mt: 2 }}>
              <RadioGroup row value={splitEntryMode} onChange={e => setSplitEntryMode(e.target.value)}>
                <FormControlLabel value="percent" control={<Radio />} label="Split by percentage" />
                <FormControlLabel value="amount" control={<Radio />} label="Split by amount" />
              </RadioGroup>

              {splitEntryMode === 'percent' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Voucher 1 %" value={voucher1Percent} onChange={e => setVoucher1Percent(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Voucher 2 %" value={100 - Number(voucher1Percent || 0)} />
                  </Grid>
                </Grid>
              )}

              {splitEntryMode === 'amount' && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Voucher 1 Amount"
                      value={voucher1Amount}
                      onChange={e => setVoucher1Amount(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      disabled
                      label="Voucher 2 Amount"
                      value={currency(selectedPostAmount - Number(voucher1Amount || 0))}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Split preview
          </Typography>

          {splitValues.map(item => (
            <Box
              key={item.id}
              sx={{
                mb: 1,
                p: 1,
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>{`Voucher ${item.id}`}</Typography>
              <Typography variant="body2">{`Percent: ${Number(item.percent || 0).toFixed(2)}%`}</Typography>
              <Typography variant="body2">{`Amount: ${currency(item.amount)}`}</Typography>
            </Box>
          ))}
        </Paper>
      </Grid>
    </Grid>
  );

  const renderStep3 = () => (
    <Grid container spacing={2}>
      {visibleVoucherDetails.map((voucher, index) => {
        const splitValue = splitValues[index] || { amount: 0, percent: 0 };

        return (
          <Grid item xs={12} md={splitMode === 'yes' ? 6 : 12} key={voucher.voucherOrderNumber || index}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {`Voucher ${index + 1} Details`}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, mb: 3 }}>
                <Typography>Amount to post</Typography>
                <Typography>{currency(splitValue.amount)}</Typography>

                <Typography>Split percent</Typography>
                <Typography>{`${Number(splitValue.percent || 0).toFixed(2)}%`}</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={splitMode === 'yes' ? 6 : 3}>
                  <InputLabel sx={{ mb: 1 }}>Payee Type</InputLabel>
                  <Select fullWidth value={voucher.payeeTypeId} onChange={e => handlePayeeTypeChange(index, e.target.value)}>
                    <MenuItem value={EXPENSES_PAYEE_TYPE.TRANSFEREE}>Transferee</MenuItem>
                    <MenuItem value={EXPENSES_PAYEE_TYPE.VENDOR}>Vendor</MenuItem>
                  </Select>
                </Grid>

                {isTransfereePayee(voucher) && (
                  <Grid item xs={12} md={splitMode === 'yes' ? 6 : 3}>
                    <InputLabel sx={{ mb: 1 }}>Account</InputLabel>
                    <Select
                      fullWidth
                      value={voucher.payeeAccount || ''}
                      onChange={e => handleTransfereeAccountSelect(index, e.target.value)}
                    >
                      {authBanks.map(v => (
                        <MenuItem key={v.bankId} value={v.bankId}>
                          {v.bankNickName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}

                {isVendorPayee(voucher) && (
                  <Grid item xs={12} md={splitMode === 'yes' ? 6 : 3}>
                    <InputLabel sx={{ mb: 1 }}>Vendor</InputLabel>
                    <AdvancedSearchComponent
                      labelText=""
                      displayValueAccessorKey="companyName"
                      saveIdValueAccessorKey="vendorId"
                      searchingCriteria={[
                        { name: 'vendorId', title: 'Vendor Id' },
                        { name: 'companyName', title: 'Company Name' },
                        { name: 'city', title: 'City' },
                        { name: 'phone', title: 'Company Phone' },
                        { name: 'email', title: 'Company Email' }
                      ]}
                      searchingCriteriaExtensions={[
                        { columnName: 'vendorId', width: 150 },
                        { columnName: 'companyName', width: 250 },
                        { columnName: 'city', width: 175 },
                        { columnName: 'phone', width: 175 },
                        { columnName: 'email', width: 175 }
                      ]}
                      searchingRows={vendors}
                      id={voucher.payToId}
                      value={getVendorName(voucher.payToId)}
                      setIdValue={id => handleVendorSelect(index, id)}
                      isReadOnly={isReadOnly}
                    />
                  </Grid>
                )}

                {isVendorPayee(voucher) && (
                  <Grid item xs={12} md={splitMode === 'yes' ? 6 : 3}>
                    <InputLabel sx={{ mb: 1 }}>Account</InputLabel>
                    <Select fullWidth value={voucher.payeeAccount || ''} onChange={e => handleVendorAccountSelect(index, e.target.value)}>
                      {vendorBanksByIndex[index]?.map(v => (
                        <MenuItem key={v.bank.bankId} value={v.bank.bankId}>
                          {v.bank.bankNickName}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                )}

                <Grid item xs={12} md={splitMode === 'yes' ? 6 : 3}>
                  <InputLabel sx={{ mb: 1 }}>Expense Code</InputLabel>
                  <AdvancedSearchComponent
                    labelText=""
                    displayValueAccessorKey={codeSearchInfo.nameKey}
                    saveIdValueAccessorKey={codeSearchInfo.idKey}
                    searchingCriteria={codeSearchInfo.searchingColumns}
                    searchingCriteriaExtensions={codeSearchInfo.searchingColumnExtensions}
                    searchingRows={codeSearchInfo.searchingRows}
                    id={voucher.expenseCode}
                    value={codeDisplayFromId(voucher.expenseCode)}
                    setIdValue={id => {
                      handleVoucherFieldChange(index, 'expenseCode', id);
                      handleVoucherFieldChange(index, 'description', codeDisplayFromId(id));
                    }}
                    isReadOnly={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <InputLabel sx={{ mb: 1 }}>Description</InputLabel>
                  <TextField
                    fullWidth
                    disabled={isReadOnly}
                    value={voucher.description || ''}
                    onChange={e => handleVoucherFieldChange(index, 'description', e.target.value)}
                  />
                </Grid>

                {voucher.isWireTransfer && (
                  <Grid item xs={12}>
                    <InputLabel sx={{ mb: 1 }}>Email Body</InputLabel>
                    <TextField
                      fullWidth
                      disabled={isReadOnly}
                      value={voucher.emailBody || ''}
                      onChange={e => handleVoucherFieldChange(index, 'emailBody', e.target.value)}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: '1px dashed #bdbdbd',
                      borderRadius: '4px',
                      p: 2
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Attachments
                    </Typography>

                    {(voucher.attachments || []).length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {voucher.attachments.map((file, fileIndex) => (
                          <Box
                            key={`${file.name}-${fileIndex}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              border: '1px solid #e0e0e0',
                              borderRadius: '4px',
                              p: 1
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AttachFileIcon fontSize="small" />
                              <Typography variant="body2">{file.name}</Typography>
                            </Box>

                            <IconButton size="small" onClick={() => removeVoucherAttachment(index, fileIndex)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        No attachments added
                      </Typography>
                    )}

                    <Button variant="outlined" size="small" onClick={() => openAttachmentDialog(index)}>
                      Add Attachment
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const renderStep4 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Step 4: Review generated report
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditAndChargesRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>{row.code !== '' ? `${row.code} - ${row.description}` : row.description}</TableCell>
                    <TableCell align="right">{currency(row.amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Total Equity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {currency(totals.totalEquity)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Already Paid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {currency(totals.alreadyPaid)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Posting Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {currency(selectedPostAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Remaining Balance</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {currency(totals.remainingBalanceAfter)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Voucher posting summary
          </Typography>

          {splitValues.map(item => (
            <Box
              key={item.id}
              sx={{
                mb: 2,
                p: 1.5,
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>{`Voucher ${item.id}`}</Typography>
              <Typography variant="body2">{`Amount: ${currency(item.amount)}`}</Typography>
              <Typography variant="body2">{`Percent: ${Number(item.percent || 0).toFixed(2)}%`}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button onClick={loadPreview} disabled={reportIsLoading}>
              Generate Report
            </Button>
            {reportIsLoading && <SpinnerComponent />}
            {!reportIsLoading && generatedReport && (
              <Box sx={{ mt: 1 }}>
                <Chip icon={<AttachFileIcon />} label={generatedReport.fileName} onClick={openPreviewWindow} variant="outlined" />
              </Box>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Post Equity Voucher
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        {`Equity Id: ${equityId || ''}`}
        {equityInfo?.equityInfo?.homesaleEquityId ? ` • Selected Record: ${equityInfo.equityInfo.homesaleEquityId}` : ''}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {isNegativeEquity ? (
        <>
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep4()}
        </>
      ) : (
        <>
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}
          {activeStep === 3 && renderStep4()}
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3
        }}
      >
        <Button disabled={activeStep === 0 || reportIsLoading || isSavingVouchers} onClick={handleBack}>
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => setShowPostVoucher(false)} disabled={reportIsLoading || isSavingVouchers}>
            Cancel
          </Button>
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" color="secondary" onClick={handleNext} disabled={reportIsLoading || isSavingVouchers}>
              Confirm
            </Button>
          ) : (
            <Button variant="contained" color="secondary" onClick={handleCreateVouchers} disabled={reportIsLoading || isSavingVouchers}>
              Create Voucher(s)
            </Button>
          )}
          {isSavingVouchers && <SpinnerComponent />}
        </Box>
      </Box>
      <Dialog open={attachmentDialogOpen} onClose={handleCloseAttachmentDialog} fullWidth maxWidth="sm">
        <DialogTitle>Upload Attachments</DialogTitle>
        <DialogContent>
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed gray',
              borderRadius: '8px',
              padding: '16px',
              textAlign: 'center',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            <input {...getInputProps()} />
            <Typography>Drag & drop files here, or click to select files</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {temporaryAttachments.length > 0 ? (
              temporaryAttachments.map((file, index) => (
                <Box
                  key={`${file.name}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    p: 1
                  }}
                >
                  <Typography variant="body2">{file.name}</Typography>
                  <IconButton size="small" onClick={() => removeTemporaryAttachment(index)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography>No files uploaded</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachmentDialog} color="secondary" variant="text">
            Cancel
          </Button>
          <Button onClick={handleSaveAttachmentDialog} color="primary" variant="contained">
            Save Attachments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = state => {
  const { expenseCodes, vendors } = state.authorizations;
  const enumerations = state.enumerations;
  return {
    expenseCodes: expenseCodes || [],
    vendors: vendors || [],
    enumerations: enumerations || {}
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    getVendorDetails,
    showToast,
    getExpenseCodesByClientId,
    getClientDetails,
    setClientId,
    createEquityVouchers,
    getHomesaleInfo,
    createEquityReport
  })
)(EquityVoucherWizard);
